import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import ZeffyLink from "@/components/ZeffyLink";
import { api } from "@/lib/api";
import {
  ORG,
  INQUIRY_TYPES,
  VOLUNTEER_AREAS,
  QUICK_ROUTES,
  SOCIAL,
} from "@/lib/constants";
import {
  Mail,
  Phone,
  Compass,
  ArrowUpRight,
  ShieldCheck,
  Clock,
  MailCheck,
  Facebook,
  Instagram,
} from "lucide-react";

const SPONSOR_TYPES = new Set(["sponsor-60th", "sponsor-ncr", "sponsor-combined", "per-event"]);
const AD_TYPE = "ad-journal";
const TABLE_TYPE = "table-sponsorship";
const VOL_TYPE = "volunteer";
const RENT_TYPE = "rent-engine-11";

// ---- Dynamic Contact hero image (May 2026, item 23) ----
// Match the inquiry context so an Active Member inquiry doesn't land on
// retiree-leaning imagery (and vice versa). For most inquiries, default to
// the neutral group rig photo (active + retired members together) — the
// user explicitly approved that one as representing the full Phoenix family.
const MEMBERSHIP_GROUP_HERO = "/photos/membership-hero.jpg";
const MEMBERSHIP_RETIREE_HERO = "/photos/membership-retiree-bg.jpg?v=2";

function resolveHeroImage(type, subject) {
  const subj = (subject || "").toLowerCase();
  if (type === "membership") {
    if (subj.includes("retiree") || subj.includes("retired")) return MEMBERSHIP_RETIREE_HERO;
    // Active Member Inquiry, or any other membership subject — use the active/group rig photo.
    return MEMBERSHIP_GROUP_HERO;
  }
  // All non-membership inquiries: use the group rig photo (neutral, represents
  // both active and retired Phoenix members, replacing the prior retiree-leaning
  // contact-detroit image).
  return MEMBERSHIP_GROUP_HERO;
}

const DEFAULT_SUBJECTS = {
  "general": "",
  "60th-interest": "60th Anniversary — Interest List",
  "sponsor-60th": "Sponsorship Inquiry — Phoenix 60th Anniversary",
  "sponsor-ncr": "Sponsorship Inquiry — 2027 NCR Conference",
  "sponsor-combined": "Sponsorship Inquiry — Combined (60th + NCR)",
  "ad-journal": "Ad Journal Placement Interest",
  "table-sponsorship": "Table Sponsorship Interest — Phoenix 60th Anniversary Gala",
  "per-event": "Per-Event Sponsorship Interest",
  "volunteer": "Volunteer Interest",
  "donation": "Donation Question",
  "membership": "Membership Inquiry",
  "partnership": "Supporter / Ally / Community Partner Inquiry",
  "project-100": "Project 100 / Recruitment",
  "rent-engine-11": "Rent Historic Engine 11",
  "share": "Share the Phoenix Mission",
  "media": "Media / Press",
};

const fieldCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 transition-colors";

const selectCls = `${fieldCls} appearance-none pr-10`;

export default function Contact() {
  const [params] = useSearchParams();
  const typeFromUrl = params.get("type");
  const subjectFromUrl = params.get("subject");

  const validType = useMemo(() => {
    const known = new Set(INQUIRY_TYPES.map((t) => t.id));
    return typeFromUrl && known.has(typeFromUrl) ? typeFromUrl : "general";
  }, [typeFromUrl]);

  // Dynamic hero image based on inquiry context (item 23, May 2026)
  const heroImage = useMemo(
    () => resolveHeroImage(validType, subjectFromUrl),
    [validType, subjectFromUrl]
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    inquiry_type: "general",
    organization: "",
    sponsor_range: "",
    preferred_track: "",
    ad_placement_type: "",
    ad_name: "",
    ad_honoree_name: "",
    ad_in_memory: false,
    table_type: "",
    table_party_size: "",
    table_org_or_family: "",
    volunteer_areas: [],
  });
  const [loading, setLoading] = useState(false);

  // Pre-fill from URL params on mount / change
  useEffect(() => {
    setForm((s) => ({
      ...s,
      inquiry_type: validType,
      subject: subjectFromUrl || DEFAULT_SUBJECTS[validType] || s.subject,
    }));
  }, [validType, subjectFromUrl]);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleVolArea = (area) => {
    setForm((s) => ({
      ...s,
      volunteer_areas: s.volunteer_areas.includes(area)
        ? s.volunteer_areas.filter((a) => a !== area)
        : [...s.volunteer_areas, area],
    }));
  };

  const buildPayload = () => {
    const extra = {};
    if (SPONSOR_TYPES.has(form.inquiry_type)) {
      if (form.sponsor_range) extra.sponsor_range = form.sponsor_range;
      if (form.preferred_track) extra.preferred_track = form.preferred_track;
    }
    if (form.inquiry_type === AD_TYPE) {
      if (form.ad_placement_type) extra.ad_placement_type = form.ad_placement_type;
      if (form.ad_name) extra.ad_name = form.ad_name;
      if (form.ad_honoree_name) extra.ad_honoree_name = form.ad_honoree_name;
      if (form.ad_in_memory) extra.ad_in_memory = true;
    }
    if (form.inquiry_type === TABLE_TYPE) {
      if (form.table_type) extra.table_type = form.table_type;
      if (form.table_party_size) extra.table_party_size = form.table_party_size;
      if (form.table_org_or_family) extra.table_org_or_family = form.table_org_or_family;
    }
    return {
      name: form.name,
      email: form.email,
      phone: form.phone,
      subject: form.subject,
      message: form.message,
      inquiry_type: form.inquiry_type,
      organization: form.organization || null,
      interest_areas: form.inquiry_type === VOL_TYPE ? form.volunteer_areas : [],
      extra_fields: extra,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", buildPayload());
      toast.success("Inquiry sent. We'll be in touch.");
      setForm((s) => ({
        ...s,
        name: "",
        email: "",
        phone: "",
        message: "",
        organization: "",
        sponsor_range: "",
        preferred_track: "",
        ad_placement_type: "",
        ad_name: "",
        ad_honoree_name: "",
        ad_in_memory: false,
        table_type: "",
        table_party_size: "",
        table_org_or_family: "",
        volunteer_areas: [],
      }));
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isSponsor = SPONSOR_TYPES.has(form.inquiry_type);
  const isAd = form.inquiry_type === AD_TYPE;
  const isTable = form.inquiry_type === TABLE_TYPE;
  const isVol = form.inquiry_type === VOL_TYPE;
  const isRental = form.inquiry_type === RENT_TYPE;

  const mapsHref = `https://www.google.com/maps?q=${encodeURIComponent(ORG.mapsQuery || ORG.address)}`;

  return (
    <div data-testid="contact-page">
      <PageMeta
        title="Contact Phoenix"
        description="Reach the Phoenix of the Detroit Fire Department — membership, Project 100, Engine 11 rentals, media, partnership, and general inquiries."
        path="/contact"
      />
      <PageHero
        overline="Contact · Inquiry Hub"
        title="Reach out to the Phoenix."
        subtitle="General inquiries, membership, Project 100, Engine 11 rentals, partnerships, media, and other Phoenix questions start here. For 2027 Conference, 60th Anniversary, gala, ad journal, table sponsorship, and event-specific inquiries, visit the dedicated 2027 page."
        image={heroImage}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <a
            href="#contact-form-anchor"
            className="btn-gold w-full sm:w-auto justify-center"
            data-testid="hero-email-phoenix"
          >
            Start Inquiry <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href="#contact-form-anchor"
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid="hero-jump-to-form"
          >
            Jump to Inquiry Form
          </a>
        </div>
      </PageHero>

      {/* ----- DIRECT CONTACT (mobile-first, also visible on desktop) ----- */}
      <section className="px-5 lg:px-8 -mt-6 lg:-mt-10" data-testid="direct-contact">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a
            href={ORG.emailConfirmed ? `mailto:${ORG.email}` : undefined}
            className={`card-dark p-5 flex items-center gap-4 ${ORG.emailConfirmed ? "hover:border-amber-500/40" : "opacity-80"}`}
            data-testid="direct-email"
            aria-disabled={!ORG.emailConfirmed}
          >
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Mail className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <div className="overline">Email</div>
              <div className="text-white text-sm mt-1 break-all">
                {ORG.emailConfirmed ? ORG.email : "Email coming soon"}
              </div>
            </span>
          </a>

          <a
            href={`tel:${ORG.phone.replace(/[^0-9+]/g, "")}`}
            className="card-dark p-5 flex items-center gap-4 hover:border-amber-500/40"
            data-testid="direct-phone"
          >
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Phone className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <div className="overline">Phone</div>
              <div className="text-white text-sm mt-1 truncate">{ORG.phone}</div>
            </span>
          </a>

          <a
            href={mapsHref}
            target="_blank"
            rel="noreferrer"
            className="card-dark p-5 flex items-center gap-4 hover:border-amber-500/40"
            data-testid="direct-map"
          >
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Compass className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <div className="overline">Map / Directions</div>
              <div className="text-white text-sm mt-1 break-words">{ORG.address}</div>
            </span>
          </a>
        </div>
      </section>

      {/* ----- MAIN LAYOUT ----- */}
      <section className="py-12 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sidebar (Quick Routes + Social) */}
          <div className="lg:col-span-4 space-y-8 lg:order-1 order-2">
            <div className="card-dark p-6" data-testid="quick-routes">
              <div className="overline">Looking for something specific?</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {QUICK_ROUTES.map((q) => {
                  const isExternal = typeof q.to === "string" && q.to.startsWith("http");
                  return (
                    <ZeffyLink
                      key={q.label}
                      href={q.to}
                      className="text-sm px-3 py-1.5 rounded-full border border-white/10 text-slate-200 hover:border-amber-500/50 hover:text-amber-400 transition-colors inline-flex items-center gap-1.5"
                      testid={`quick-route-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {q.label}
                      {isExternal && <ArrowUpRight className="h-3 w-3 opacity-70" />}
                    </ZeffyLink>
                  );
                })}
              </div>
            </div>

            {/* ----- SOCIAL ----- */}
            <div className="card-dark p-6" data-testid="contact-social">
              <div className="overline">Connect on Social</div>
              <div className="mt-4 flex flex-col gap-3">
                {SOCIAL.facebookUrl ? (
                  <a
                    href={SOCIAL.facebookUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors text-sm"
                    data-testid="contact-social-facebook"
                  >
                    <Facebook className="h-4 w-4 text-amber-500" />
                    <span>Facebook · <span className="text-amber-400">{SOCIAL.facebookLabel}</span></span>
                  </a>
                ) : (
                  <div className="flex items-start gap-3 text-slate-400 text-sm" data-testid="contact-social-facebook-label">
                    <Facebook className="h-4 w-4 text-amber-500 mt-0.5" />
                    <div>
                      <div className="text-slate-200">Facebook</div>
                      <div className="text-xs text-slate-500">{SOCIAL.facebookLabel}</div>
                    </div>
                  </div>
                )}
                {SOCIAL.instagram && (
                  <a
                    href={SOCIAL.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 text-slate-300 hover:text-amber-400 transition-colors text-sm"
                    data-testid="contact-social-instagram"
                  >
                    <Instagram className="h-4 w-4 text-amber-500" />
                    <span>Instagram · <span className="text-amber-400">{SOCIAL.instagramHandle}</span></span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8 lg:order-2 order-1">
            <div id="contact-form-anchor" className="card-dark p-7 lg:p-10">
              <div className="overline">Inquiry Form</div>
              <h3 className="font-display text-3xl text-white mt-2">Tell us how we can help.</h3>

              <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
                {/* Inquiry Type */}
                <div className="md:col-span-2">
                  <label className="block overline mb-2">Inquiry Type</label>
                  <div className="relative">
                    <select
                      name="inquiry_type"
                      value={form.inquiry_type}
                      onChange={onChange}
                      className={selectCls}
                      data-testid="contact-inquiry-type"
                    >
                      {INQUIRY_TYPES.map((t) => (
                        <option key={t.id} value={t.id} className="bg-[#1A1A1A]">{t.label}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-amber-500">▾</span>
                  </div>
                </div>

                {/* Rental nudge */}
                {isRental && (
                  <div className="md:col-span-2 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <span className="text-slate-200 text-sm flex-1">
                      For the full rental inquiry form (event date, guest count, event type), visit the Rent Historic Engine 11 page.
                    </span>
                    <Link to="/rent-engine-11" className="btn-gold text-sm" data-testid="contact-rental-link">
                      Open Rental Form <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}

                {/* Core fields */}
                <input name="name" value={form.name} onChange={onChange} required placeholder="Full Name" className={fieldCls} data-testid="contact-name" />
                <input name="email" type="email" value={form.email} onChange={onChange} required placeholder="Email" className={fieldCls} data-testid="contact-email" />
                <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone (optional)" className={fieldCls} data-testid="contact-phone" />
                <input name="subject" value={form.subject} onChange={onChange} required placeholder="Subject" className={fieldCls} data-testid="contact-subject" />

                {/* Sponsorship fields */}
                {isSponsor && (
                  <>
                    <input name="organization" value={form.organization} onChange={onChange} placeholder="Organization / Business Name" className={fieldCls} data-testid="contact-org" />
                    <input name="sponsor_range" value={form.sponsor_range} onChange={onChange} placeholder="Sponsorship Interest Level or Range (optional)" className={fieldCls} data-testid="contact-sponsor-range" />
                    <div className="md:col-span-2">
                      <label className="block overline mb-2">Preferred Track</label>
                      <div className="relative">
                        <select name="preferred_track" value={form.preferred_track} onChange={onChange} className={selectCls} data-testid="contact-track">
                          <option value="" className="bg-[#1A1A1A]">Select preferred track…</option>
                          <option value="60th" className="bg-[#1A1A1A]">Phoenix 60th Anniversary</option>
                          <option value="ncr" className="bg-[#1A1A1A]">2027 NCR Conference</option>
                          <option value="combined" className="bg-[#1A1A1A]">Combined / Both</option>
                        </select>
                        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-amber-500">▾</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Ad journal fields */}
                {isAd && (
                  <>
                    <div className="md:col-span-2">
                      <label className="block overline mb-2">Preferred Placement Type</label>
                      <div className="relative">
                        <select name="ad_placement_type" value={form.ad_placement_type} onChange={onChange} className={selectCls} data-testid="contact-ad-placement">
                          <option value="" className="bg-[#1A1A1A]">Select placement type…</option>
                          {["Full Page", "Half Page", "Quarter Page", "Business Card", "Booster Listing", "Honoree or In-Memory Tribute"].map((p) => (
                            <option key={p} value={p} className="bg-[#1A1A1A]">{p}</option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-amber-500">▾</span>
                      </div>
                    </div>
                    <input name="ad_name" value={form.ad_name} onChange={onChange} placeholder="Business, Family, or Individual Name" className={fieldCls} data-testid="contact-ad-name" />
                    <input name="ad_honoree_name" value={form.ad_honoree_name} onChange={onChange} placeholder="Honoree Name (if applicable)" className={fieldCls} data-testid="contact-ad-honoree" />
                    <label className="md:col-span-2 flex items-center gap-3 text-slate-300 text-sm">
                      <input type="checkbox" name="ad_in_memory" checked={form.ad_in_memory} onChange={onChange} className="h-4 w-4 accent-amber-500" data-testid="contact-ad-memory" />
                      This is an in-memory tribute placement.
                    </label>
                  </>
                )}

                {/* Table fields */}
                {isTable && (
                  <>
                    <div className="md:col-span-2">
                      <label className="block overline mb-2">Preferred Table Type</label>
                      <div className="relative">
                        <select name="table_type" value={form.table_type} onChange={onChange} className={selectCls} data-testid="contact-table-type">
                          <option value="" className="bg-[#1A1A1A]">Select table type…</option>
                          {["Patron Table", "Phoenix Table", "Community Table", "Legacy Table"].map((p) => (
                            <option key={p} value={p} className="bg-[#1A1A1A]">{p}</option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-amber-500">▾</span>
                      </div>
                    </div>
                    <input name="table_party_size" type="number" min="1" value={form.table_party_size} onChange={onChange} placeholder="Approximate Party Size" className={fieldCls} data-testid="contact-table-party" />
                    <input name="table_org_or_family" value={form.table_org_or_family} onChange={onChange} placeholder="Organization or Family Name" className={fieldCls} data-testid="contact-table-org" />
                  </>
                )}

                {/* Volunteer fields */}
                {isVol && (
                  <div className="md:col-span-2">
                    <label className="block overline mb-3">Areas of Interest</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="contact-volunteer-areas">
                      {VOLUNTEER_AREAS.map((a) => {
                        const active = form.volunteer_areas.includes(a);
                        return (
                          <label
                            key={a}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${active ? "border-amber-500/60 bg-amber-500/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-amber-500"
                              checked={active}
                              onChange={() => toggleVolArea(a)}
                              data-testid={`vol-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            />
                            <span className="text-slate-200 text-sm">{a}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Message */}
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={6}
                  placeholder="Your message..."
                  className={`${fieldCls} md:col-span-2`}
                  data-testid="contact-message"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold md:col-span-2 justify-center disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? "Sending..." : "Send Inquiry"}
                </button>

                {/* Expectation copy */}
                <div className="md:col-span-2 space-y-3 mt-2 text-sm text-slate-400">
                  <div className="flex items-start gap-3" data-testid="contact-response-copy">
                    <Clock className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                    <span>We typically respond within 5–7 business days. For urgent matters, please call {ORG.phone}.</span>
                  </div>
                  <div className="flex items-start gap-3" data-testid="contact-confirmation-copy">
                    <MailCheck className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                    <span>You will receive an email confirmation once your inquiry is received.</span>
                  </div>
                  <div className="flex items-start gap-3" data-testid="contact-privacy-copy">
                    <ShieldCheck className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                    <span>
                      By submitting this form, you acknowledge that Phoenix may
                      use the information provided to respond to your inquiry
                      in accordance with our{" "}
                      <Link
                        to="/privacy"
                        className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
                        data-testid="contact-privacy-link"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
